import React, { forwardRef, useEffect, useState } from 'react';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import styled, { css } from 'styled-components';

import { colors, media, Option, Theme } from '../../../core';
import Button from '../Button';
import CheckboxInput from '../CheckboxInput';
import Icon from '../Icon';
import Label from '../Label';
import Text from '../Text';

export interface SelectInputProps {
  className?: string;
  disabled?: boolean;
  width?: string;
  label?: string;
  animatedLabel?: string;
  labelType?: 'outside' | 'inside';
  options?: Option[] | { label: string; options: Option[] };
  defaultOptions?: Option[];
  defaultValue?: Option | string;
  onChange?: (selectedOption: Option) => void;
  variant?: 'primary' | 'secondary';
  isSearchable?: boolean;
  isClearable?: boolean;
  isAsync?: boolean;
  placeholder?: string;
  showRequired?: boolean;
  isError?: string;
  errorMessage?: string;
  isMulti?: boolean;
  getOptionLabel?: () => void;
  getOptionValue?: () => void;
  loadOptions?: { [key: string]: string }[] | any;
  closeMenuOnSelect?: boolean;
  hideSelectedOptions?: boolean;
  onKeyPress?: (e: any) => void;
  errorMessageOnDisable?: string;
  value?: Option | null;
  backspaceRemovesValue?: boolean;
  secondaryOption?: {
    label: string;
    handleOnclick?: (option: Option) => void;
  };
}

const baseSmallLabelStyles = css`
  ${({ theme }) => `
    font-size: 12px;
    top: 0;
    color: ${theme.fontColor || colors.ash};
  `}
`;

const StyledIcon = styled(Icon).attrs(({ theme, type }) => ({
  color: theme.selectIndicatorIconColor || colors.primary,
  type: type ? type : 'caret-down' || theme.selectIndicatorIcon,
  width: theme.selectIndicatorIconViewWidth || 20,
  height: theme.selectIndicatorIconViewHeight || 20,
  size: theme.selectIndicatorIconSize || 20,
}))`
  margin-top: 0;
  cursor: pointer;
`;

const SelectInputLayout = styled.div<{ width?: string; hasEnteredText: boolean }>`
  width: ${({ width }) => width || '100%'};
  position: relative;

  ${media.tabletPortrait} {
    width: 100%;
  }

  ${({ hasEnteredText }) =>
    hasEnteredText &&
    css`
      ${AnimatedLabel} {
        ${baseSmallLabelStyles};
      }
    `}
`;

const StyledLabel = styled(Label)<{
  labelType?: 'outside' | 'inside';
  variant?: 'primary' | 'secondary';
}>`
  ${({ labelType, variant, theme }) =>
    labelType === 'outside' &&
    variant === 'primary' &&
    theme.name !== Theme.theme1 &&
    `
      font-size: 12px;
      font-weight: 400;
      padding: 0 16px;
      margin: 0;
      position: absolute;
      top: -8px;
      line-height: 1.5;
  `}

  ${({ labelType, variant }) =>
    labelType === 'inside' &&
    variant === 'secondary' &&
    `
      position: absolute;
      top: 7px;
      left: 20px;
      width: 80px;
      margin: 0;
      line-height: 1.5;

      ${media.tabletPortrait} {
        left: 12px;
        top: 12px;
      }
  `}
`;

const AnimatedLabel = styled(Label)`
  position: absolute;
  top: 50%;
  left: 16px;
  transform: translateY(-50%);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.5px;
  line-height: 2;
  transition: top 300ms ease-in-out, font-size 300ms ease-in-out, opacity 300ms ease-in-out;
  color: ${({ theme }) => theme.textInputColor || colors.ash};
  z-index: -1;
`;

const StyledOptionContainer = styled.div<{ isFocused?: boolean }>`
  align-items: center;
  background-color: ${({ isFocused }) => (isFocused ? colors.pearl : colors.white)};
  display: flex;
  justify-content: space-between;
  padding-right: 5px;
`;

const DropdownIndicator = (props: any) => {
  return (
    <components.DropdownIndicator {...props}>
      <StyledIcon />
    </components.DropdownIndicator>
  );
};

const SearchIndicator = (props: any) => {
  return (
    !props.hasValue && (
      <components.DropdownIndicator {...props}>
        <StyledIcon type="search" color={colors.primary} />
      </components.DropdownIndicator>
    )
  );
};

const ClearIndicator = (props: any) => {
  return (
    <components.ClearIndicator {...props}>
      <StyledIcon type="remove" color={colors.primary} />
    </components.ClearIndicator>
  );
};

const InputOption = (props: any) => {
  const onChange = () => {};

  return (
    <components.Option {...props}>
      <CheckboxInput
        id={props.value}
        checked={props.isSelected}
        label={props.label}
        name={props.value}
        onChange={onChange}
      />
    </components.Option>
  );
};

const MultiValue = (props: any) => {
  return (
    <components.MultiValue {...props}>
      <Text noMargin size="xsmall">
        {props.data.label}
      </Text>
    </components.MultiValue>
  );
};

const Control = (props: any) => {
  return (
    <React.Fragment>
      <components.Control {...props} />
      {props.selectProps.animatedLabel && (
        <AnimatedLabel>
          {props.selectProps.animatedLabel}
          {props.selectProps.showRequired && '*'}
        </AnimatedLabel>
      )}
    </React.Fragment>
  );
};

const NoOptionsMessage = (props: any) => {
  return (
    <React.Fragment>
      {props.selectProps.inputValue && (
        <components.NoOptionsMessage {...props}>
          <Text noMargin>No Results Found</Text>
        </components.NoOptionsMessage>
      )}
    </React.Fragment>
  );
};

const AsyncOption = (props: any) => {
  if (!!props.selectProps.secondaryOption) {
    return (
      <StyledOptionContainer isFocused={props.isFocused}>
        <components.Option {...props} />

        <Text noMargin size="medium" weight="700">
          {props.data.label}
        </Text>

        <Button
          variant="secondary"
          size="xsmall"
          onClick={() => props.selectProps.secondaryOption.handleOnclick(props.data)}
        >
          {props.selectProps.secondaryOption.label}
        </Button>
      </StyledOptionContainer>
    );
  }

  return <components.Option {...props} />;
};

const buttonStylesPrimary = ({ isError }: SelectInputProps) => css`
  .react-select {
    &__input {
      color: ${({ theme }) => theme.selectColor || colors.ash};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
    }

    &__multi-value__label {
      padding: 0 3px;
      p {
        padding: 0;
      }
    }

    &__control {
      min-height: ${({ theme }) => theme.selectInputHeight || '36px'};
      height: auto;
      cursor: pointer;
      border: ${({ theme }) =>
        `1px solid ${isError ? theme.errorColor : theme.selectPrimaryBorderColor || colors.smoke}`};
      border-radius: 3px;
      background-color: ${({ theme }) => theme.selectInputBackgroundColor || colors.white};
      padding: ${({ theme }) => theme.selectInputPadding || '3px 8px'};

      ${({ theme }) =>
        theme.selectShowOnlyBottomBorder &&
        `
          border-top: 0;
          border-left: 0;
          border-right: 0;
          border-radius: 0;
      `}

      &:hover {
        box-shadow: ${({ theme }) =>
          theme.selectHoverShadow || '0 2px 4px 0 rgba(217, 217, 217, 0.5)'};
        border-color: ${({ theme }) => theme.selectPrimaryBorderColor || colors.smoke};
        border-bottom-color: ${({ theme }) => theme.selectPrimaryBorderColor || colors.smoke};
      }

      &--is-focused,
      &--is-focused:hover {
        box-shadow: none;
        outline: 0;
        border-color: ${({ theme }) => theme.selectPrimaryFocusedBorderColor || colors.primary};
        border-bottom-color: ${({ theme }) =>
          theme.selectPrimaryFocusedBorderColor || colors.primary};

        .react-select__single-value {
          border-color: ${({ theme }) => theme.selectBorderColor || colors.charcoal};
        }

        & ~ ${AnimatedLabel} {
          ${baseSmallLabelStyles};
        }
      }

      &--is-disabled {
        background-color: ${({ theme }) =>
          theme.selectPrimaryDisabledBackgroundColor || colors.pearl};
        border-color: ${({ theme }) => theme.selectDisabledBorderColor || colors.cloud};
        border-bottom-color: ${({ theme }) => theme.selectDisabledBorderColor || colors.cloud};
        box-shadow: none;

        .react-select__single-value {
          color: ${({ theme }) => theme.selectDisabledColor || colors.smoke};
        }
      }

      &--is-disabled svg path {
        fill: ${({ theme }) => theme.selectDisabledColor || colors.smoke};
      }

      &--menu-is-open {
        .react-select__indicator {
          ${({ theme }) =>
            theme.selectShowIndicatorAnimation &&
            `
            transform: rotate(-180deg);
          `}
        }
      }
    }

    &__value-container {
      padding: 3px 8px;
      height: 34px;
    }

    &__placeholder,
    &__single-value,
    &__option {
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      line-height: ${({ theme }) => theme.fontLineHeight || '1.5'};
      font-weight: ${({ theme }) => theme.fontWeight || `600`};
    }

    &__placeholder {
      color: ${({ theme }) => theme.selectPlaceholderColor || colors.smoke};
    }

    &__single-value {
      color: ${({ theme }) => theme.selectColor || colors.ash};
    }

    &__indicator-separator {
      display: none;
    }

    &__indicator {
      padding: 7px 8px;
      ${({ theme }) =>
        theme.selectShowIndicatorAnimation &&
        `
        transform: rotate(0);
      `}
    }

    &__menu {
      border-radius: 0 0 3px 3px;
      margin-top: 0;
      overflow: hidden;
      transform: translateZ(0);
      display: block;
      box-shadow: ${({ theme }) => theme.selectMenuShadow || 'none'};
      border: ${({ theme }) => theme.selectMenuBorder || `1px solid ${colors.cloud}`};
      border-top: 0;

      &-notice--no-options {
        color: ${({ theme }) => theme.fontColor || colors.ash};
        font-size: ${({ theme }) => theme.fontSize || '14px'};
        font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
        padding: 16px 10px;
        display: none;
      }
    }

    &__menu-list {
      padding: 0;
    }

    &__option {
      background-color: ${colors.white};
      text-align: left;
      color: ${({ theme }) => theme.fontColor || colors.ash};
      cursor: pointer;
      padding: ${({ theme }) => theme.selectOptionPadding || '16px 10px'};

      &--is-focused {
        background-color: ${colors.pearl};
      }

      &:active {
        background-color: ${colors.cloud};
      }

      &--is-selected,
      &--is-selected:active {
        font-weight: 600;
        color: ${({ theme }) => theme.selectOptionActiveColor || colors.charcoal};
      }
    }

    &__group-heading {
      color: ${({ theme }) => theme.selectColor || colors.charcoal};
      font-size: 12px;
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
      padding: 8px 20px;
      font-weight: bold;
    }
  }
`;

const buttonStylesSecondary = ({ labelType }: SelectInputProps) => css`
  .react-select {
    &__input {
      color: ${({ theme }) => theme.selectColor || colors.ash};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
    }

    &__multi-value__label {
      padding: 0 3px;
      p {
        padding: 0;
      }
    }

    &__control {
      min-height: 36px;
      height: auto;
      cursor: pointer;
      border: 1px solid transparent;
      border-radius: 3px 3px 0 0;
      border-bottom-color: ${colors.cloud};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      padding: ${({ theme }) => theme.selectInputPadding || '3px 8px'};
      background-color: transparent;
      height: auto;

      ${media.tabletPortrait} {
        padding: 12px;
        border: 0;
        border-bottom: 1px solid ${colors.cloud};
      }

      ${() =>
        labelType === 'inside' &&
        `
          padding-left: 100px;

          ${media.tabletPortrait} {
            padding-left: 100px;
          }
        `}

      &:hover {
        box-shadow: 0 2px 4px 0 rgba(217, 217, 217, 0.5);
        border-color: ${colors.cloud};
        border-bottom-color: ${colors.cloud};
      }

      &--is-focused,
      &--is-focused:hover {
        box-shadow: none;
        outline: 0;
        border-color: ${colors.cloud};
        border-bottom-color: ${colors.cloud};

        .react-select__single-value {
          border-color: 'transparent';
        }
      }

      &--is-disabled {
        background-color: 'transparent';
        border-color: 'transparent';
        border-bottom-color: ${colors.cloud};
        box-shadow: none;

        .react-select__single-value {
          color: ${colors.smoke};
        }
      }

      &--is-disabled svg path {
        fill: ${colors.smoke};
      }

      &--menu-is-open {
        .react-select__indicator {
          transform: rotate(-180deg);
          transition: transform 0.3s;
        }
      }
    }

    &__value-container {
      padding: 0;
    }

    &__placeholder,
    &__single-value,
    &__option {
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
      font-size: ${({ theme }) => theme.fontSize || '14px'};
      line-height: ${({ theme }) => theme.fontLineHeight || '1.5'};
      font-weight: ${({ theme }) => theme.fontWeight || `600`};
    }

    &__placeholder {
      color: ${({ theme }) => theme.selectPlaceholderColor || colors.smoke};
    }

    &__single-value {
      color: ${({ theme }) => theme.selectColor || colors.ash};
    }

    &__indicator-separator {
      display: none;
    }

    &__indicator {
      padding: 7px 8px;
      transform: rotate(0);
      transition: transform 0.3s;
    }

    &__menu {
      border-radius: 0 0 3px 3px;
      margin-top: 0;
      overflow: hidden;
      transform: translateZ(0);
      display: block;
      border: 1px solid ${colors.cloud};
      border-top: 0;
      box-shadow: none;

      ${media.tabletPortrait} {
        background-color: ${colors.pearl};
        border: 0;
      }

      &-notice--no-options {
        text-align: left;
        color: ${({ theme }) => theme.fontColor || colors.ash};
        font-size: ${({ theme }) => theme.fontSize || '14px'};
        font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
        padding: 8px 27px;
      }
    }

    &__menu-list {
      padding: 0;
    }

    &__option {
      background-color: ${colors.white};
      text-align: left;
      color: ${({ theme }) => theme.fontColor || colors.ash};
      cursor: pointer;
      padding: 8px 27px;

      ${media.tabletPortrait} {
        padding: 20px 27px;
        background-color: ${colors.pearl};
      }

      &:hover {
        background-color: ${colors.pearl};
      }

      &:active {
        background-color: ${colors.pearl};
      }

      &--is-selected,
      &--is-selected:active {
        font-weight: 600;
        color: ${({ theme }) => theme.selectOptionActiveColor || colors.charcoal};
      }
    }

    &__group-heading {
      color: ${({ theme }) => theme.selectColor || colors.charcoal};
      font-size: 12px;
      font-family: ${({ theme }) => theme.fontFamily || `Quicksand, san-serif`};
      padding: 8px 20px;
      font-weight: bold;
    }
  }
`;

const ErrorMessage = styled(Text)`
  margin: 0;
  margin-top: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.errorColor};
  padding: ${({ theme }) => theme.inputErrorMessagePadding || '0'};
  line-height: 1.5;
`;

const StyledReactSelect = styled(ReactSelect).attrs({
  classNamePrefix: 'react-select',
  components: { DropdownIndicator, Control },
})<SelectInputProps>`
  ${({ variant }) => (variant === 'primary' ? buttonStylesPrimary : buttonStylesSecondary)}
`;

const StyledAsyncSelect = styled(AsyncSelect).attrs({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator: SearchIndicator,
    ClearIndicator,
    NoOptionsMessage,
    Option: AsyncOption,
  },
})<SelectInputProps>`
  ${({ variant }) => (variant === 'primary' ? buttonStylesPrimary : buttonStylesSecondary)}
`;

const StyledAsyncMultiSelect = styled(AsyncSelect).attrs({
  classNamePrefix: 'react-select',
  components: {
    DropdownIndicator: SearchIndicator,
    ClearIndicator,
    Option: InputOption,
    MultiValue,
    NoOptionsMessage,
  },
})<SelectInputProps>`
  ${({ variant }) => (variant === 'primary' ? buttonStylesPrimary : buttonStylesSecondary)}
`;

const SelectInput = forwardRef(
  (
    {
      className,
      disabled,
      width,
      label,
      animatedLabel,
      labelType = 'outside',
      onChange,
      isAsync = false,
      showRequired,
      errorMessage,
      variant = 'primary',
      isSearchable = true,
      ...inputProps
    }: SelectInputProps,
    ref: any
  ) => {
    const [inputValue, setInputValue] = useState(inputProps.defaultValue || inputProps.value);
    const [hasEnteredText, setHasEnteredText] = useState(false);

    useEffect(() => {
      checkUserInput();
    }, [inputValue]);

    const checkUserInput = () => {
      if (inputValue) {
        setHasEnteredText(true);
      } else {
        setHasEnteredText(false);
      }
    };

    const renderInput = () => {
      if (isAsync) {
        if (inputProps.isMulti) {
          return (
            <StyledAsyncMultiSelect
              ref={ref}
              isDisabled={disabled}
              isError={!!errorMessage}
              onChange={(val: Option) => {
                setInputValue(val);
                onChange && onChange(val);
              }}
              labelType={labelType}
              variant={variant}
              {...inputProps}
            />
          );
        }

        return (
          <StyledAsyncSelect
            ref={ref}
            isDisabled={disabled}
            isError={!!errorMessage}
            onChange={(val: Option) => {
              setInputValue(val);
              onChange && onChange(val);
            }}
            labelType={labelType}
            variant={variant}
            {...inputProps}
          />
        );
      } else {
        return (
          <StyledReactSelect
            ref={ref}
            isDisabled={disabled}
            isError={!!errorMessage}
            onChange={(val: Option) => {
              setInputValue(val);
              onChange && onChange(val);
            }}
            labelType={labelType}
            variant={variant}
            animatedLabel={animatedLabel}
            showRequired={showRequired}
            isSearchable={isSearchable}
            {...inputProps}
          />
        );
      }
    };

    return (
      <SelectInputLayout className={className} width={width} hasEnteredText={hasEnteredText}>
        {label && (
          <StyledLabel labelType={labelType} variant={variant}>
            {label}
            {showRequired && '*'}
          </StyledLabel>
        )}
        {renderInput()}
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </SelectInputLayout>
    );
  }
);

export default SelectInput;
